/**
 * LogisticsBookings (N3, Oct 2026) — standalone management screen for
 * vehicle transport bookings, under Logistics → "Bookings".
 *
 *  • Six server-driven count cards (GET /logistics-bookings/summary) —
 *    counts are never derived from the list payload.
 *  • Filters: search, status (defaults to Active = the six live stages;
 *    clearing to "All" sends NO status param, which per the PO
 *    convention returns everything including DELETED), transporter,
 *    origin branch, and the shared DateRangePicker.
 *  • Row/detail actions follow the backend contract: Edit only on
 *    DRAFT, Delete on anything except DELETED, and the detail dialog
 *    surfaces only the single next valid lifecycle action.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import DateRangePicker from "@/components/DateRangePicker";
import PermButton from "@/components/PermButton";
import BookingFormDialog from "@/components/BookingFormDialog";
import BookingDetailDialog, { STATUS_COLORS, STATUS_LABELS } from "@/components/BookingDetailDialog";
import { toast } from "sonner";
import { Plus, Search, Eye, Pencil, Trash2, RefreshCw, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = ["DRAFT", "CONFIRMED", "VEHICLE_REPORTED", "DISPATCHED", "REPORTED", "UNLOADED"];

const LogisticsBookings = () => {
  const [list, setList] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(false);
  // Default: Active (non-DELETED). Clearing to ALL sends no status
  // param → server returns everything incl. DELETED (PO convention).
  const [statusFilter, setStatusFilter] = useState("ACTIVE");
  const [search, setSearch] = useState("");
  const [transporter, setTransporter] = useState("");
  const [branch, setBranch] = useState("");
  // Same branch source + inline dropdown as IBT / Reports (/api/branches/names).
  const [branches, setBranches] = useState([]);

  useEffect(() => {
    axios.get(`${API}/branches/names`)
      .then((res) => setBranches([...new Set(res.data?.branches || [])]))
      .catch(() => setBranches([]));
  }, []);
  const [dateRange, setDateRange] = useState(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [editBooking, setEditBooking] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const buildParams = useCallback((includeStatus) => {
    const p = {};
    if (search.trim()) p.search = search.trim();
    if (includeStatus && statusFilter !== "ALL") p.status = statusFilter;
    if (transporter.trim()) p.transporter = transporter.trim();
    if (branch.trim()) p.branch = branch.trim();
    if (dateRange?.preset === "custom" && dateRange.from && dateRange.to) {
      p.date_preset = "custom";
      p.date_from = dateRange.from;
      p.date_to = dateRange.to;
    } else if (dateRange?.preset && dateRange.preset !== "custom") {
      p.date_preset = dateRange.preset;
    }
    return p;
  }, [search, statusFilter, transporter, branch, dateRange]);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [listRes, sumRes] = await Promise.all([
        axios.get(`${API}/logistics-bookings`, { params: buildParams(true) }),
        axios.get(`${API}/logistics-bookings/summary`, { params: buildParams(false) }),
      ]);
      setList(listRes.data);
      setCounts(sumRes.data.counts || {});
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load bookings");
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  // Debounce text-input changes; selects/date apply on the same tick.
  useEffect(() => {
    const t = setTimeout(refresh, 300);
    return () => clearTimeout(t);
  }, [refresh]);

  const clearFilters = () => {
    setSearch("");
    setTransporter("");
    setBranch("");
    setDateRange(null);
    setStatusFilter("ACTIVE");
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/logistics-bookings/${deleteTarget.id}`);
      toast.success(`Booking ${deleteTarget.booking_code} deleted`);
      setDeleteTarget(null);
      refresh();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : detail?.message || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  const total = STATUSES.reduce((n, s) => n + (counts[s] || 0), 0);

  return (
    <div className="p-6 space-y-5" data-testid="logistics-bookings-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Logistics Bookings</h1>
          <p className="text-sm text-zinc-500">
            Vehicle transport bookings across DC, IBT, Dispatch Lots and Dispatch Drafts.
          </p>
        </div>
        <PermButton entity="LogisticsBooking" action="CREATE"
          className="bg-blue-600 hover:bg-blue-700 text-white"
          onClick={() => setCreateOpen(true)} data-testid="new-booking-btn">
          <Plus className="h-4 w-4 mr-1" /> New Booking
        </PermButton>
      </div>

      {/* Six server-driven stage counts (DELETED deliberately not shown). */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="summary-cards">
        {STATUSES.map((s) => (
          <Card
            key={s}
            className={`cursor-pointer transition-shadow hover:shadow-md ${statusFilter === s ? "ring-2 ring-blue-500" : ""}`}
            onClick={() => setStatusFilter(statusFilter === s ? "ACTIVE" : s)}
            data-testid={`summary-card-${s.toLowerCase()}`}
          >
            <CardContent className="p-4">
              <div className="text-xs uppercase tracking-wide text-zinc-500">{STATUS_LABELS[s]}</div>
              <div className="text-2xl font-semibold" data-testid={`summary-count-${s.toLowerCase()}`}>
                {counts[s] ?? 0}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2" data-testid="booking-filters">
        <div className="relative">
          <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-zinc-400" />
          <Input
            className="pl-8 w-64"
            placeholder="Search code, transporter, route…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="filter-search"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48" data-testid="filter-status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ACTIVE" data-testid="filter-status-active">Active (not deleted)</SelectItem>
            <SelectItem value="ALL" data-testid="filter-status-all">All (incl. deleted)</SelectItem>
            {STATUSES.map((s) => (
              <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>
            ))}
            <SelectItem value="DELETED" data-testid="filter-status-deleted">Deleted</SelectItem>
          </SelectContent>
        </Select>
        <Input
          className="w-44"
          placeholder="Transporter"
          value={transporter}
          onChange={(e) => setTransporter(e.target.value)}
          data-testid="filter-transporter"
        />
        <Select value={branch || "ALL"} onValueChange={(v) => setBranch(v === "ALL" ? "" : v)}>
          <SelectTrigger className="w-48" data-testid="filter-branch">
            <SelectValue placeholder="Origin branch" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL" data-testid="filter-branch-all">All branches</SelectItem>
            {branches.map((b) => (
              <SelectItem key={b} value={b} data-testid={`filter-branch-option-${b}`}>{b}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <DateRangePicker value={dateRange} onChange={setDateRange} />
        <PermButton entity="LogisticsBooking" action="READ" variant="outline" size="sm"
          onClick={clearFilters} data-testid="clear-filters-btn">
          <X className="h-4 w-4 mr-1" /> Clear
        </PermButton>
        <PermButton entity="LogisticsBooking" action="READ" variant="outline" size="sm"
          onClick={refresh} data-testid="refresh-btn">
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
        </PermButton>
      </div>

      <div className="text-sm text-zinc-500" data-testid="list-meta">
        {list.length} booking{list.length === 1 ? "" : "s"} · {total} active overall
      </div>

      <div className="rounded-md border overflow-x-auto">
        <table className="w-full text-sm" data-testid="bookings-table">
          <thead className="bg-zinc-50 text-left">
            <tr>
              <th className="px-3 py-2 font-medium">Code</th>
              <th className="px-3 py-2 font-medium">Status</th>
              <th className="px-3 py-2 font-medium">Source</th>
              <th className="px-3 py-2 font-medium">Route</th>
              <th className="px-3 py-2 font-medium">Vehicle</th>
              <th className="px-3 py-2 font-medium">Transporter</th>
              <th className="px-3 py-2 font-medium">Created</th>
              <th className="px-3 py-2 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && (
              <tr>
                <td colSpan={8} className="px-3 py-8 text-center text-zinc-500" data-testid="bookings-empty">
                  {loading ? "Loading…" : "No bookings match the current filters."}
                </td>
              </tr>
            )}
            {list.map((b) => (
              <tr key={b.id} className="border-t hover:bg-zinc-50" data-testid={`booking-row-${b.id}`}>
                <td className="px-3 py-2 font-mono text-xs">{b.booking_code}</td>
                <td className="px-3 py-2">
                  <Badge className={`border ${STATUS_COLORS[b.status] || ""}`} data-testid={`booking-status-${b.id}`}>
                    {STATUS_LABELS[b.status] || b.status}
                  </Badge>
                </td>
                <td className="px-3 py-2 text-xs">
                  {b.source_type ? `${b.source_type} ${b.source_id}` : "Standalone"}
                </td>
                <td className="px-3 py-2 text-xs">
                  {[b.from_location, b.to_location].filter(Boolean).join(" → ") || "—"}
                </td>
                <td className="px-3 py-2 text-xs">{b.vehicle_type ? `${b.vehicle_type} ft` : "—"}</td>
                <td className="px-3 py-2 text-xs">{b.transporter_name || "—"}</td>
                <td className="px-3 py-2 text-xs">
                  {b.created_at ? new Date(b.created_at).toLocaleDateString() : "—"}
                </td>
                <td className="px-3 py-2">
                  <div className="flex justify-end gap-1">
                    <PermButton entity="LogisticsBooking" action="READ" variant="ghost" size="sm"
                      onClick={() => setDetailId(b.id)} data-testid={`booking-view-${b.id}`}>
                      <Eye className="h-4 w-4" />
                    </PermButton>
                    {b.status === "DRAFT" && (
                      <PermButton entity="LogisticsBooking" action="UPDATE" variant="ghost" size="sm"
                        onClick={() => setEditBooking(b)} data-testid={`booking-edit-${b.id}`}>
                        <Pencil className="h-4 w-4" />
                      </PermButton>
                    )}
                    {b.status !== "DELETED" && (
                      <PermButton entity="LogisticsBooking" action="DELETE" variant="ghost" size="sm"
                        className="text-rose-600 hover:bg-rose-50"
                        onClick={() => setDeleteTarget(b)} data-testid={`booking-delete-${b.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </PermButton>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <BookingFormDialog
        open={createOpen || Boolean(editBooking)}
        onClose={() => { setCreateOpen(false); setEditBooking(null); }}
        booking={editBooking}
        branches={branches}
        onSaved={refresh}
      />
      <BookingDetailDialog
        open={Boolean(detailId)}
        onClose={() => setDetailId(null)}
        bookingId={detailId}
        onChanged={refresh}
        onEdit={(b) => { setDetailId(null); setEditBooking(b); }}
        onDelete={(b) => { setDetailId(null); setDeleteTarget(b); }}
      />

      <Dialog open={Boolean(deleteTarget)} onOpenChange={(o) => { if (!deleting && !o) setDeleteTarget(null); }}>
        <DialogContent className="max-w-md" data-testid="booking-delete-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Delete {deleteTarget?.booking_code}?</DialogTitle>
            <DialogDescription>
              This is a soft delete: the booking moves to DELETED with a full audit trail,
              and its source record becomes available for a new booking.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <PermButton entity="LogisticsBooking" action="DELETE" variant="outline"
              onClick={() => setDeleteTarget(null)} disabled={deleting}>
              Cancel
            </PermButton>
            <PermButton entity="LogisticsBooking" action="DELETE"
              className="bg-rose-600 hover:bg-rose-700 text-white"
              onClick={handleDelete} disabled={deleting} data-testid="booking-delete-confirm-btn">
              {deleting ? "Deleting…" : "Delete booking"}
            </PermButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LogisticsBookings;
